import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { RRule, Weekday } from 'rrule';
import { GlassSelect } from '@/components/ui/glass-select';
import { GlassDatePicker } from '@/components/ui/glass-date-picker';
import { GlassTimePicker } from '@/components/ui/glass-time-picker';
import { MentionEditor } from '@/components/tasks/mention-editor';
import { ProcessOrCaseSelect, type ProcessOrCaseValue } from '@/components/tasks/process-or-case-select';

import {
  apiClient,
  type CreateTaskInput,
  type TaskCategory,
  type TaskPriority,
  type UpdateTaskInput,
} from '@/lib/api-client';

const PRIORITY_LABEL: Record<TaskPriority, string> = {
  low: 'Baixa',
  normal: 'Normal',
  high: 'Alta',
  urgent: 'Urgente',
};

interface TaskFormModalProps {
  categories: TaskCategory[];
  onClose: () => void;
  onCreated: () => void;
  currentUserId: string;
  tenantId: string;
  /** Pré-preenche o vínculo com processo. */
  caseId?: string | null;
  /** Pré-preenche o vínculo com cliente. */
  clientId?: string | null;
  /** Pré-preenche o vínculo com intimação. */
  intimacaoId?: string | null;
  /** Pré-preenche o prazo fatal (ISO YYYY-MM-DD). */
  initialFatalDate?: string | null;
  /**
   * Valores iniciais para campos D06.
   * Quando aberto a partir da agenda, vem com dueDate pré-preenchido.
   */
  defaultValues?: Partial<{
    dueDate: string;
    dueTime: string;
    allDay: boolean;
  }>;
  /**
   * Quando definido, o modal opera em modo EDIÇÃO:
   * busca a tarefa pelo ID, popula o form e chama updateTask no submit.
   */
  editingTaskId?: string | null;
}

type FormState = CreateTaskInput;

type RecurFreq = 'DAILY' | 'WEEKLY' | 'MONTHLY';
type RecurEnd = 'never' | 'until' | 'count';

interface RecurConfig {
  freq: RecurFreq;
  interval: number;
  weekdays: number[]; // RRule.MO.weekday etc (0=MO...6=SU)
  endType: RecurEnd;
  until: string; // YYYY-MM-DD
  count: number;
}

const WEEKDAY_LABELS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];
const RRULE_WEEKDAYS = [RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR, RRule.SA, RRule.SU];

function buildRRuleString(cfg: RecurConfig): string {
  const freq =
    cfg.freq === 'DAILY'
      ? RRule.DAILY
      : cfg.freq === 'WEEKLY'
        ? RRule.WEEKLY
        : RRule.MONTHLY;

  const options: ConstructorParameters<typeof RRule>[0] = {
    freq,
    interval: cfg.interval || 1,
  };

  if (cfg.freq === 'WEEKLY' && cfg.weekdays.length > 0) {
    options.byweekday = cfg.weekdays.map((i) => RRULE_WEEKDAYS[i] as Weekday);
  }

  if (cfg.endType === 'until' && cfg.until) {
    options.until = new Date(cfg.until + 'T23:59:59Z');
  } else if (cfg.endType === 'count' && cfg.count > 0) {
    options.count = cfg.count;
  }

  const rule = new RRule(options);
  // toString() retorna "DTSTART:...\nRRULE:..." — pegar só a parte RRULE
  const str = rule.toString();
  const match = str.match(/RRULE:(.+)/);
  return match ? match[1] : str;
}

function parseRRuleString(ruleStr: string): RecurConfig {
  const cfg: RecurConfig = {
    freq: 'WEEKLY',
    interval: 1,
    weekdays: [],
    endType: 'never',
    until: '',
    count: 0,
  };
  try {
    const full = ruleStr.startsWith('RRULE:') ? ruleStr : `RRULE:${ruleStr}`;
    const rule = RRule.fromString(full);
    const opts = rule.origOptions;
    if (opts.freq === RRule.DAILY) cfg.freq = 'DAILY';
    else if (opts.freq === RRule.MONTHLY) cfg.freq = 'MONTHLY';
    else cfg.freq = 'WEEKLY';
    cfg.interval = (opts.interval as number) ?? 1;
    if (opts.byweekday) {
      const days = Array.isArray(opts.byweekday) ? opts.byweekday : [opts.byweekday];
      cfg.weekdays = (days as Weekday[]).map((d) => (d as Weekday).weekday);
    }
    if (opts.until) {
      cfg.endType = 'until';
      const d = opts.until as Date;
      cfg.until = d.toISOString().slice(0, 10);
    } else if (opts.count) {
      cfg.endType = 'count';
      cfg.count = opts.count as number;
    }
  } catch {
    // fallback: retorna config padrao
  }
  return cfg;
}

const EMPTY_FORM = (
  currentUserId: string,
  overrides: Partial<FormState> = {},
): FormState => ({
  title: '',
  description: '',
  priority: 'normal',
  categoryId: null,
  dueDate: null,
  fatalDate: null,
  assigneeUserIds: currentUserId ? [currentUserId] : [],
  caseId: null,
  clientId: null,
  intimacaoId: null,
  dueTime: null,
  allDay: false,
  endAt: null,
  location: null,
  showInAgenda: false,
  isImportant: false,
  isUrgent: false,
  isPrivate: false,
  isRecurring: false,
  recurrenceRule: null,
  isFuture: false,
  isRetroactive: false,
  ...overrides,
});

const DEFAULT_RECUR_CONFIG: RecurConfig = {
  freq: 'WEEKLY',
  interval: 1,
  weekdays: [],
  endType: 'never',
  until: '',
  count: 1,
};

/**
 * Modal de criação/edição de tarefa.
 *
 * Modo criação: `editingTaskId` ausente ou null — comportamento original.
 * Modo edição:  `editingTaskId` definido — busca a tarefa, popula form,
 *               chama `tasksUpdate` no submit.
 */
export function TaskFormModal({
  categories,
  onClose,
  onCreated,
  currentUserId,
  tenantId,
  caseId = null,
  clientId = null,
  intimacaoId = null,
  initialFatalDate = null,
  defaultValues,
  editingTaskId = null,
}: TaskFormModalProps) {
  const isEditing = !!editingTaskId;
  const queryClient = useQueryClient();

  // Quando caseId/clientId vêm de fora, o campo fica read-only
  const hasExternalLink = !!(caseId || clientId);

  const [form, setForm] = useState<FormState>(
    EMPTY_FORM(currentUserId, {
      dueDate: defaultValues?.dueDate ?? null,
      fatalDate: initialFatalDate,
      caseId,
      clientId,
      intimacaoId,
      dueTime: defaultValues?.dueTime ?? null,
      allDay: defaultValues?.allDay ?? false,
    }),
  );

  // Estado do ProcessOrCaseSelect
  const [processOrCase, setProcessOrCase] = useState<ProcessOrCaseValue>({
    caseId: caseId ?? null,
    clientId: clientId ?? null,
    freeText: '',
  });

  // Controla se o campo "Informar término" está habilitado
  const [informarTermino, setInformarTermino] = useState(false);
  const [populated, setPopulated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recurConfig, setRecurConfig] = useState<RecurConfig>({ ...DEFAULT_RECUR_CONFIG });

  // Sincroniza seleção de processo/cliente no form
  function handleProcessOrCaseChange(next: ProcessOrCaseValue) {
    setProcessOrCase(next);
    setForm((f) => ({ ...f, caseId: next.caseId, clientId: next.clientId }));
  }

  // Busca dados existentes quando em modo edição
  const { data: existingTask, isLoading: loadingTask } = useQuery({
    queryKey: ['task', editingTaskId],
    queryFn: () => apiClient.tasksGet(editingTaskId!),
    enabled: isEditing,
    staleTime: 30_000,
  });

  // Membros do escritório para o @-mention e multi-select de responsáveis
  const { data: membersData } = useQuery({
    queryKey: ['tenant-members', tenantId],
    queryFn: () => apiClient.tenantsListMembers(tenantId),
    staleTime: 5 * 60_000,
    enabled: !!tenantId,
  });
  const mentionMembers = (membersData ?? []).map((m) => ({
    id: m.userId,
    name: m.user.name,
  }));

  // Popula o form quando os dados chegam (apenas uma vez)
  useEffect(() => {
    if (!existingTask || populated) return;
    setForm({
      title: existingTask.title,
      description: existingTask.description ?? '',
      priority: existingTask.priority,
      categoryId: existingTask.categoryId,
      dueDate: existingTask.dueDate,
      fatalDate: existingTask.fatalDate,
      assigneeUserIds: existingTask.assignees.map((a) => a.userId),
      caseId: existingTask.caseId,
      clientId: existingTask.clientId,
      intimacaoId: existingTask.intimacaoId,
      dueTime: existingTask.dueTime,
      allDay: existingTask.allDay,
      endAt: existingTask.endAt,
      location: existingTask.location,
      showInAgenda: existingTask.showInAgenda,
      isImportant: existingTask.isImportant,
      isUrgent: existingTask.isUrgent,
      isPrivate: existingTask.isPrivate,
      isRecurring: existingTask.isRecurring,
      recurrenceRule: existingTask.recurrenceRule ?? null,
      isFuture: existingTask.isFuture ?? false,
      isRetroactive: existingTask.isRetroactive ?? false,
    });
    // Popula o ProcessOrCaseSelect com o vínculo existente
    setProcessOrCase({
      caseId: existingTask.caseId ?? null,
      clientId: existingTask.clientId ?? null,
      freeText:
        !existingTask.caseId && !existingTask.clientId
          ? (existingTask.processReference ?? '')
          : '',
    });
    // Ativa "Informar término" se havia endAt
    if (existingTask.endAt && !existingTask.allDay) {
      setInformarTermino(true);
    }
    // Parseia recurrenceRule para o config visual
    if (existingTask.recurrenceRule) {
      setRecurConfig(parseRRuleString(existingTask.recurrenceRule));
    }
    setPopulated(true);
  }, [existingTask, populated]);

  // Mutation de criação
  const createMut = useMutation({
    mutationFn: (input: CreateTaskInput) => apiClient.tasksCreate(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agenda', 'events'] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      onCreated();
    },
    onError: (err: unknown) => {
      const m = err instanceof Error ? err.message : 'Falha ao criar';
      setError(m);
    },
  });

  // Mutation de atualização
  const updateMut = useMutation({
    mutationFn: (input: UpdateTaskInput) =>
      apiClient.tasksUpdate(editingTaskId!, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task', editingTaskId] });
      queryClient.invalidateQueries({ queryKey: ['agenda', 'events'] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      onCreated();
    },
    onError: (err: unknown) => {
      const m = err instanceof Error ? err.message : 'Falha ao salvar';
      setError(m);
    },
  });

  const isPending = createMut.isPending || updateMut.isPending;

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.categoryId) {
      setError('Selecione o tipo de tarefa');
      return;
    }
    if (!form.assigneeUserIds || form.assigneeUserIds.length === 0) {
      setError('Selecione pelo menos um responsável');
      return;
    }
    // Preenche title com o nome da categoria selecionada
    const selectedCategory = categories.find((c) => c.id === form.categoryId);
    const titleFromCategory = selectedCategory?.name ?? '';

    // Monta recurrenceRule a partir da config visual
    const recurrenceRule =
      form.isRecurring ? buildRRuleString(recurConfig) : null;

    // processReference: só persiste quando não há caseId/clientId vinculado
    const processReference =
      processOrCase.caseId || processOrCase.clientId
        ? null
        : processOrCase.freeText.trim() || null;

    const payload = {
      ...form,
      title: titleFromCategory,
      description: form.description?.trim() || null,
      dueDate: form.dueDate || null,
      fatalDate: form.fatalDate || null,
      dueTime: form.allDay ? null : (form.dueTime || null),
      endAt: (form.allDay || !informarTermino) ? null : (form.endAt || null),
      recurrenceRule,
      processReference,
    };

    if (isEditing) {
      updateMut.mutate(payload);
    } else {
      createMut.mutate(payload);
    }
  }

  const inputCls =
    'w-full rounded border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-sm text-[var(--fg)]';
  const labelCls = 'block text-xs font-medium text-[var(--fg-muted)] mb-1';
  const checkboxRowCls =
    'flex cursor-pointer items-center gap-1.5 text-sm text-[var(--fg)] select-none';

  // Exibe skeleton enquanto carrega dados no modo edição
  if (isEditing && loadingTask && !populated) {
    return (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
        <div className="bg-[var(--modal-bg,var(--card-bg))] border border-[var(--modal-border,var(--card-border))] backdrop-blur-[var(--blur)] rounded-lg w-full max-w-[70vw] lg:max-w-4xl min-w-[640px] p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-[var(--fg)]">
              Editar tarefa
            </h2>
            <button type="button" onClick={onClose} aria-label="Fechar">
              <X className="h-5 w-5 text-[var(--fg-muted)]" />
            </button>
          </div>
          <p className="text-sm text-[var(--fg-muted)]">Carregando…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <form
        onSubmit={submit}
        className="bg-[var(--modal-bg,var(--card-bg))] border border-[var(--modal-border,var(--card-border))] backdrop-blur-[var(--blur)] rounded-lg w-full max-w-[70vw] lg:max-w-4xl min-w-[640px] p-6 space-y-4 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-[var(--fg)]">
            {isEditing ? 'Editar tarefa' : 'Nova tarefa'}
          </h2>
          <button type="button" onClick={onClose} aria-label="Fechar">
            <X className="h-5 w-5 text-[var(--fg-muted)]" />
          </button>
        </div>

        {/* 1. Processo ou caso */}
        <div>
          <label className={labelCls}>Processo ou caso</label>
          <ProcessOrCaseSelect
            value={processOrCase}
            onChange={handleProcessOrCaseChange}
            placeholder="Vincular a um processo ou cliente"
            readOnly={hasExternalLink && !isEditing}
          />
        </div>

        {/* 2. Responsáveis (multi-select simples via checkboxes) */}
        <div>
          <label className={labelCls}>Adicionar responsáveis *</label>
          {membersData && membersData.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {membersData.map((m) => {
                const selected = (form.assigneeUserIds ?? []).includes(m.userId);
                return (
                  <button
                    key={m.userId}
                    type="button"
                    onClick={() => {
                      const current = form.assigneeUserIds ?? [];
                      const next = selected
                        ? current.filter((id) => id !== m.userId)
                        : [...current, m.userId];
                      setForm({ ...form, assigneeUserIds: next });
                    }}
                    className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                      selected
                        ? 'border-blue-500 bg-blue-600 text-white'
                        : 'border-[var(--border)] text-[var(--fg-muted)] hover:border-blue-400 hover:text-[var(--fg)]'
                    }`}
                  >
                    {m.user.name}
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="text-xs text-[var(--fg-muted)]">Carregando membros…</p>
          )}
        </div>

        {/* 3. Tarefa (tipo/categoria obrigatório) */}
        <div>
          <label className={labelCls}>Tarefa *</label>
          <GlassSelect
            value={form.categoryId ?? ''}
            onChange={(v) => setForm({ ...form, categoryId: v || null })}
            placeholder="— Selecione o tipo —"
            options={categories.map((c) => ({ value: c.id, label: c.name }))}
          />
        </div>

        {/* 4. Data / Hora / Prazo fatal */}
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className={labelCls}>Data compromisso</label>
            <GlassDatePicker
              value={form.dueDate ?? ''}
              onChange={(v) => setForm({ ...form, dueDate: v || null })}
              placeholder="DD/MM/AAAA"
            />
          </div>
          <div>
            <label className={labelCls}>Hora</label>
            <GlassTimePicker
              value={form.dueTime ?? ''}
              onChange={(v) => setForm({ ...form, dueTime: v || null })}
              disabled={!!form.allDay}
              placeholder="HH:MM"
            />
          </div>
          <div>
            <label className={labelCls}>Prazo fatal</label>
            <GlassDatePicker
              value={form.fatalDate ?? ''}
              onChange={(v) => setForm({ ...form, fatalDate: v || null })}
              placeholder="DD/MM/AAAA"
            />
          </div>
        </div>

        {/* 5. Toggles de agenda */}
        <div className="flex flex-wrap gap-4">
          <label className={checkboxRowCls}>
            <input
              type="checkbox"
              checked={informarTermino}
              onChange={(e) => {
                setInformarTermino(e.target.checked);
                if (!e.target.checked) setForm({ ...form, endAt: null });
              }}
              disabled={!!form.allDay}
              className="accent-[var(--accent)] disabled:opacity-40"
            />
            Informar término
          </label>
          <label className={checkboxRowCls}>
            <input
              type="checkbox"
              checked={!!form.allDay}
              onChange={(e) => {
                const allDay = e.target.checked;
                setForm({ ...form, allDay, dueTime: null, endAt: null });
                if (allDay) setInformarTermino(false);
              }}
              className="accent-[var(--accent)]"
            />
            Dia inteiro
          </label>
        </div>

        {/* Campo de término (visível apenas quando informarTermino=true e allDay=false) */}
        {informarTermino && !form.allDay && (
          <div>
            <label className={labelCls}>Término (data e hora)</label>
            <div className="grid grid-cols-2 gap-2">
              <GlassDatePicker
                value={form.endAt ? form.endAt.slice(0, 10) : ''}
                onChange={(d) => {
                  const time = form.endAt ? form.endAt.slice(11, 16) : '00:00';
                  setForm({ ...form, endAt: d ? `${d}T${time}` : null });
                }}
                placeholder="DD/MM/AAAA"
              />
              <GlassTimePicker
                value={form.endAt ? form.endAt.slice(11, 16) : ''}
                onChange={(t) => {
                  const date = form.endAt ? form.endAt.slice(0, 10) : '';
                  setForm({ ...form, endAt: date ? `${date}T${t}` : null });
                }}
                placeholder="HH:MM"
              />
            </div>
          </div>
        )}

        {/* 6. Local */}
        <div>
          <label className={labelCls}>Local</label>
          <input
            type="text"
            value={form.location ?? ''}
            onChange={(e) =>
              setForm({ ...form, location: e.target.value || null })
            }
            className={inputCls}
            maxLength={500}
            placeholder="Opcional"
          />
        </div>

        {/* 7. Descrição com @-mention */}
        <div>
          <label className={labelCls}>Descrição (digite @ para mencionar alguém)</label>
          <MentionEditor
            value={form.description ?? ''}
            onChange={(html) => setForm({ ...form, description: html })}
            members={mentionMembers}
            placeholder="Descrição da tarefa…"
            rows={3}
          />
        </div>

        {/* Prioridade */}
        <div>
          <label className={labelCls}>Prioridade</label>
          <GlassSelect
            value={form.priority}
            onChange={(v) => setForm({ ...form, priority: v as TaskPriority })}
            options={Object.entries(PRIORITY_LABEL).map(([k, v]) => ({
              value: k,
              label: v,
            }))}
          />
        </div>

        {/* 9. Flags — ordem ADVBOX: Importante / Urgente / Futura / Recorrente / Privada / Retroativa */}
        <div className="flex flex-wrap gap-4 pt-1 border-t border-[var(--border)]">
          <label className={checkboxRowCls} title="Marcar tarefa como importante">
            <input
              type="checkbox"
              checked={!!form.isImportant}
              onChange={(e) => setForm({ ...form, isImportant: e.target.checked })}
              className="accent-[var(--accent)]"
            />
            Importante
          </label>
          <label className={checkboxRowCls} title="Marcar tarefa como urgente">
            <input
              type="checkbox"
              checked={!!form.isUrgent}
              onChange={(e) => setForm({ ...form, isUrgent: e.target.checked })}
              className="accent-[var(--accent)]"
            />
            Urgente
          </label>
          <label className={checkboxRowCls} title="Tarefa para o futuro sem data definida">
            <input
              type="checkbox"
              checked={!!form.isFuture}
              onChange={(e) => setForm({ ...form, isFuture: e.target.checked })}
              className="accent-[var(--accent)]"
            />
            Futura
          </label>
          <label className={checkboxRowCls} title="Tarefa que se repete periodicamente">
            <input
              type="checkbox"
              checked={!!form.isRecurring}
              onChange={(e) =>
                setForm({ ...form, isRecurring: e.target.checked })
              }
              className="accent-[var(--accent)]"
            />
            Recorrente
          </label>
          <label className={checkboxRowCls} title="Tarefa visível apenas para você">
            <input
              type="checkbox"
              checked={!!form.isPrivate}
              onChange={(e) => setForm({ ...form, isPrivate: e.target.checked })}
              className="accent-[var(--accent)]"
            />
            Privada
          </label>
          <label className={checkboxRowCls} title="Registro retroativo — atividade já executada">
            <input
              type="checkbox"
              checked={!!form.isRetroactive}
              onChange={(e) => setForm({ ...form, isRetroactive: e.target.checked })}
              className="accent-[var(--accent)]"
            />
            Retroativa
          </label>
        </div>

        {/* 10. Configuracao de recorrencia (visivel quando isRecurring=true) */}
        {form.isRecurring && (
          <div className="rounded-md border border-[var(--border)] bg-[color-mix(in_oklch,var(--fg)_3%,transparent)] p-3 space-y-3">
            <p className="text-xs font-semibold text-[var(--fg-muted)] uppercase tracking-wide">
              Configurar recorrência
            </p>

            {/* Frequencia + Intervalo */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Frequência</label>
                <GlassSelect
                  value={recurConfig.freq}
                  onChange={(v) =>
                    setRecurConfig({ ...recurConfig, freq: v as RecurFreq })
                  }
                  options={[
                    { value: 'DAILY', label: 'Diária' },
                    { value: 'WEEKLY', label: 'Semanal' },
                    { value: 'MONTHLY', label: 'Mensal' },
                  ]}
                />
              </div>
              <div>
                <label className={labelCls}>A cada</label>
                <input
                  type="number"
                  min={1}
                  max={99}
                  value={recurConfig.interval}
                  onChange={(e) =>
                    setRecurConfig({
                      ...recurConfig,
                      interval: Math.max(1, Number(e.target.value)),
                    })
                  }
                  className={inputCls}
                />
              </div>
            </div>

            {/* Dias da semana (so Semanal) */}
            {recurConfig.freq === 'WEEKLY' && (
              <div>
                <label className={labelCls}>Dias da semana</label>
                <div className="flex gap-1.5 flex-wrap">
                  {WEEKDAY_LABELS.map((lbl, idx) => {
                    const active = recurConfig.weekdays.includes(idx);
                    return (
                      <button
                        key={lbl}
                        type="button"
                        onClick={() => {
                          const next = active
                            ? recurConfig.weekdays.filter((d) => d !== idx)
                            : [...recurConfig.weekdays, idx];
                          setRecurConfig({ ...recurConfig, weekdays: next });
                        }}
                        className={`rounded px-2 py-1 text-xs font-medium border transition ${
                          active
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'border-[var(--border)] text-[var(--fg-muted)] hover:border-blue-400 hover:text-[var(--fg)]'
                        }`}
                      >
                        {lbl}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Termino */}
            <div>
              <label className={labelCls}>Termina</label>
              <div className="flex flex-col gap-2">
                <label className={checkboxRowCls}>
                  <input
                    type="radio"
                    name="recur-end"
                    checked={recurConfig.endType === 'never'}
                    onChange={() =>
                      setRecurConfig({ ...recurConfig, endType: 'never' })
                    }
                    className="accent-[var(--accent)]"
                  />
                  Nunca
                </label>
                <label className={checkboxRowCls}>
                  <input
                    type="radio"
                    name="recur-end"
                    checked={recurConfig.endType === 'until'}
                    onChange={() =>
                      setRecurConfig({ ...recurConfig, endType: 'until' })
                    }
                    className="accent-[var(--accent)]"
                  />
                  Em uma data
                  {recurConfig.endType === 'until' && (
                    <span className="ml-2 inline-block w-36">
                      <GlassDatePicker
                        value={recurConfig.until}
                        onChange={(v) =>
                          setRecurConfig({ ...recurConfig, until: v })
                        }
                        placeholder="DD/MM/AAAA"
                      />
                    </span>
                  )}
                </label>
                <label className={checkboxRowCls}>
                  <input
                    type="radio"
                    name="recur-end"
                    checked={recurConfig.endType === 'count'}
                    onChange={() =>
                      setRecurConfig({ ...recurConfig, endType: 'count' })
                    }
                    className="accent-[var(--accent)]"
                  />
                  Após
                  {recurConfig.endType === 'count' && (
                    <>
                      <input
                        type="number"
                        min={1}
                        max={999}
                        value={recurConfig.count}
                        onChange={(e) =>
                          setRecurConfig({
                            ...recurConfig,
                            count: Math.max(1, Number(e.target.value)),
                          })
                        }
                        className="ml-2 w-16 rounded border border-[var(--border)] bg-[var(--surface)] px-2 py-1 text-xs text-[var(--fg)]"
                      />
                      <span className="text-sm text-[var(--fg-muted)]">ocorrências</span>
                    </>
                  )}
                </label>
              </div>
            </div>
          </div>
        )}

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded text-sm text-[var(--fg-muted)] hover:bg-[color-mix(in_oklch,var(--fg)_6%,transparent)]"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={
              isPending ||
              !form.categoryId ||
              !form.assigneeUserIds ||
              form.assigneeUserIds.length === 0
            }
            className="px-4 py-2 rounded bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            {isPending
              ? isEditing
                ? 'Salvando...'
                : 'Criando...'
              : isEditing
              ? 'Salvar alterações'
              : 'Criar tarefa'}
          </button>
        </div>
      </form>
    </div>
  );
}
